import { ActivatedRoute, NavigationExtras, Params, Router } from '@angular/router';

import { buildListingSlug } from './listing-slug.util';

export const LISTING_RETURN_URL_STATE_KEY = 'listingReturnUrl';

const FEATURED_LISTING_PREFIX = '/featured-properties/listing';
const FOR_RENT_LISTING_PREFIX = '/for-rent/listing';
const FEATURED_BUILDING_PREFIX = '/featured-properties/building';
const FOR_RENT_BUILDING_PREFIX = '/for-rent/building';
const LISTING_DETAIL_ROUTE_PATH = 'listing/:listingId/:slug';
const BUILDING_DETAIL_ROUTE_PATH = 'building/:buildingSlug';

function isListingDetailRoutePath(path: string): boolean {
  return path === LISTING_DETAIL_ROUTE_PATH || path === 'listing/:listingId' || path.startsWith('listing/');
}

function isBuildingDetailRoutePath(path: string): boolean {
  return path === BUILDING_DETAIL_ROUTE_PATH || path.startsWith('building/');
}

/** Listing or building overlay routed under the browse host. */
function isBrowseOverlayRoutePath(path: string): boolean {
  return isListingDetailRoutePath(path) || isBuildingDetailRoutePath(path);
}

export function hasListingDetailInRouteTree(route: ActivatedRoute): boolean {
  let current: ActivatedRoute | null = route;

  while (current) {
    const path = current.routeConfig?.path ?? '';
    if (isListingDetailRoutePath(path)) {
      return true;
    }
    current = current.firstChild;
  }

  return false;
}

export function hasBrowseOverlayInRouteTree(route: ActivatedRoute): boolean {
  let current: ActivatedRoute | null = route;

  while (current) {
    const path = current.routeConfig?.path ?? '';
    if (isBrowseOverlayRoutePath(path)) {
      return true;
    }
    current = current.firstChild;
  }

  return false;
}

function urlLooksLikeListingDetail(url: string): boolean {
  return url.includes(FEATURED_LISTING_PREFIX) || url.includes(FOR_RENT_LISTING_PREFIX);
}

function urlLooksLikeBrowseOverlay(url: string): boolean {
  return (
    urlLooksLikeListingDetail(url)
    || url.includes(FEATURED_BUILDING_PREFIX)
    || url.includes(FOR_RENT_BUILDING_PREFIX)
  );
}

export function isListingDetailRouteActive(
  router: Router,
  route?: ActivatedRoute | null,
): boolean {
  if (route && hasListingDetailInRouteTree(route)) {
    return true;
  }

  if (urlLooksLikeListingDetail(router.url)) {
    return true;
  }

  const navigation = router.getCurrentNavigation();
  const finalUrl = navigation?.finalUrl?.toString() ?? '';
  return urlLooksLikeListingDetail(finalUrl);
}

/** True while a listing or building overlay is open over browse filters. */
export function isBrowseOverlayRouteActive(
  router: Router,
  route?: ActivatedRoute | null,
): boolean {
  if (route && hasBrowseOverlayInRouteTree(route)) {
    return true;
  }

  if (urlLooksLikeBrowseOverlay(router.url)) {
    return true;
  }

  const navigation = router.getCurrentNavigation();
  const finalUrl = navigation?.finalUrl?.toString() ?? '';
  return urlLooksLikeBrowseOverlay(finalUrl);
}

export function buildListingPublicPath(property: object): string {
  const record = property as Record<string, unknown>;
  const listingId = String(record['ListingID'] ?? '');
  const slug = buildListingSlug(record);
  return `${FEATURED_LISTING_PREFIX}/${listingId}/${slug}/`;
}

export function buildListingRoute(property: object): (string | number)[] {
  const record = property as Record<string, unknown>;
  const listingId = String(record['ListingID'] ?? '');
  return ['/featured-properties/listing', listingId, buildListingSlug(record)];
}

export function buildListingShareUrl(property: object): string {
  return `${window.location.origin}${buildListingPublicPath(property)}`;
}

export function buildListingRouteById(
  listingId: string,
  slug = 'listing',
): (string | number)[] {
  return ['/featured-properties/listing', listingId, slug];
}

/** Relative listing segments for an overlay host (featured-properties or for-rent). */
export function buildFeaturedListingSegments(
  property: object,
): (string | number)[] {
  const record = property as Record<string, unknown>;
  const listingId = String(record['ListingID'] ?? '');
  return ['listing', listingId, buildListingSlug(record)];
}

export function featuredPropertiesShellRoute(route: ActivatedRoute): ActivatedRoute | null {
  let current: ActivatedRoute | null = route;

  while (current) {
    const isShell = current.routeConfig?.path === '';
    const underFeatured = current.parent?.snapshot.url?.some(
      (segment) => segment.path === 'featured-properties',
    );

    if (isShell && underFeatured) {
      return current;
    }

    current = current.parent;
  }

  return null;
}

/** Browse route that owns the listing overlay outlet (featured or for-rent). */
export function listingOverlayHostRoute(route: ActivatedRoute): ActivatedRoute | null {
  let current: ActivatedRoute | null = route;

  while (current) {
    const childPaths = current.routeConfig?.children?.map((child) => child.path ?? '') ?? [];
    if (childPaths.some((path) => isBrowseOverlayRoutePath(path))) {
      return current;
    }
    current = current.parent;
  }

  return null;
}

function routeTreeContainsSegment(route: ActivatedRoute, segment: string): boolean {
  let current: ActivatedRoute | null = route;

  while (current) {
    if (current.routeConfig?.path === segment) {
      return true;
    }
    if (current.snapshot?.url?.some((part) => part.path === segment)) {
      return true;
    }
    current = current.parent;
  }

  return false;
}

export function buildFeaturedPropertiesBrowseReturnUrl(
  shellRoute: ActivatedRoute,
): string {
  return buildPathWithQueryParams('/featured-properties', shellRoute.snapshot.queryParams);
}

function buildPathWithQueryParams(path: string, queryParams: Params): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(queryParams)) {
    if (value === null || value === undefined || value === '') {
      continue;
    }

    if (Array.isArray(value)) {
      for (const entry of value) {
        if (entry !== null && entry !== undefined && entry !== '') {
          search.append(key, String(entry));
        }
      }
      continue;
    }

    search.set(key, String(value));
  }

  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

export interface BuildListingNavigationOptions {
  returnUrl?: string;
}

function buildListingNavigationExtras(
  hostRoute: ActivatedRoute | null,
  options?: BuildListingNavigationOptions,
): NavigationExtras | undefined {
  const state = options?.returnUrl
    ? { [LISTING_RETURN_URL_STATE_KEY]: options.returnUrl }
    : undefined;

  if (hostRoute) {
    return {
      relativeTo: hostRoute,
      queryParamsHandling: 'preserve',
      ...(state ? { state } : {}),
    };
  }

  return state
    ? { state, queryParamsHandling: 'preserve' }
    : { queryParamsHandling: 'preserve' };
}

function defaultOverlayReturnUrl(
  route: ActivatedRoute,
  hostRoute: ActivatedRoute,
): string | undefined {
  const shellRoute = featuredPropertiesShellRoute(route);
  if (shellRoute) {
    return buildFeaturedPropertiesBrowseReturnUrl(shellRoute);
  }

  if (routeTreeContainsSegment(hostRoute, 'for-rent')) {
    return '/for-rent';
  }

  return undefined;
}

export function buildListingNavigation(
  property: object,
  route: ActivatedRoute,
  options?: BuildListingNavigationOptions,
): { commands: (string | number)[]; extras?: NavigationExtras } {
  const hostRoute = listingOverlayHostRoute(route);
  const resolvedOptions: BuildListingNavigationOptions = { ...options };

  if (hostRoute && !resolvedOptions.returnUrl) {
    resolvedOptions.returnUrl = defaultOverlayReturnUrl(route, hostRoute);
  }

  const extras = buildListingNavigationExtras(hostRoute, resolvedOptions);

  if (hostRoute) {
    return {
      commands: buildFeaturedListingSegments(property),
      extras,
    };
  }

  return {
    commands: buildListingRoute(property),
    extras,
  };
}

export function listingReturnUrlFromState(state: unknown): string | null {
  if (!state || typeof state !== 'object') {
    return null;
  }

  const returnUrl = (state as Record<string, unknown>)[LISTING_RETURN_URL_STATE_KEY];
  return typeof returnUrl === 'string' && returnUrl.startsWith('/') ? returnUrl : null;
}

/**
 * Close a listing/building overlay without wiping browse filter query params.
 * Same-host returns (browse or building) keep the current query string.
 * Cross-page returns (favorites, etc.) still use navigateByUrl.
 */
export function closeBrowseOverlay(
  router: Router,
  route: ActivatedRoute,
  returnUrl: string | null | undefined,
): void {
  const hostRoute = listingOverlayHostRoute(route);
  if (hostRoute) {
    const hostPath = normalizeBrowsePath(
      router.serializeUrl(router.createUrlTree(['.'], { relativeTo: hostRoute })),
    );
    if (isSameBrowseHostReturn(returnUrl, hostPath)) {
      const childCommands = browseOverlayChildCommands(returnUrl, hostPath);
      void router.navigate(childCommands.length > 0 ? childCommands : ['.'], {
        relativeTo: hostRoute,
        queryParamsHandling: 'preserve',
      });
      return;
    }
  }

  if (returnUrl) {
    void router.navigateByUrl(returnUrl);
    return;
  }

  void router.navigateByUrl('/featured-properties');
}

function normalizeBrowsePath(path: string): string {
  const withoutQuery = path.split('?')[0] || '/';
  return withoutQuery.replace(/\/$/, '') || '/';
}

function isSameBrowseHostReturn(
  returnUrl: string | null | undefined,
  hostPath: string,
): boolean {
  if (!returnUrl) {
    return true;
  }
  const returnPath = normalizeBrowsePath(returnUrl);
  return returnPath === hostPath || returnPath.startsWith(`${hostPath}/`);
}

function browseOverlayChildCommands(
  returnUrl: string | null | undefined,
  hostPath: string,
): string[] {
  if (!returnUrl) {
    return [];
  }
  const returnPath = normalizeBrowsePath(returnUrl);
  if (returnPath === hostPath) {
    return [];
  }
  if (!returnPath.startsWith(`${hostPath}/`)) {
    return [];
  }
  return returnPath.slice(hostPath.length + 1).split('/').filter(Boolean);
}
